// Voice editor — 2. Tone & style section. Extracted verbatim from
// `VoiceEditorPage.tsx` (no behavior change).

import { useT } from '@barel/i18n';
import { FieldAssembly, Segmented, Switch } from '@barel/ui';

import { SectionShell, type SectionPropsWithVoice } from './SectionShell';

export function ToneSection({ voice, flush }: SectionPropsWithVoice) {
  const t = useT('voice');
  return (
    <SectionShell title={t('tone.title')} subtitle={t('tone.subtitle')}>
      <div className="flex flex-col gap-4">
        <FieldAssembly label={t('tone.toneLabel')} description={t('tone.toneSub')} asChild>
          <div role="group" aria-label={t('tone.toneLabel')}>
            <Segmented<'formal' | 'semi' | 'casual'>
              value={voice.tone}
              onValueChange={(next) => {
                void flush({ tone: next });
              }}
              options={[
                { value: 'formal', label: t('tone.toneFormal') },
                { value: 'semi', label: t('tone.toneSemi') },
                { value: 'casual', label: t('tone.toneCasual') },
              ]}
              aria-label={t('tone.toneLabel')}
              size="sm"
            />
          </div>
        </FieldAssembly>
        <FieldAssembly label={t('tone.lengthLabel')} description={t('tone.lengthSub')} asChild>
          <div role="group" aria-label={t('tone.lengthLabel')}>
            <Segmented<'short' | 'medium' | 'mirror'>
              value={voice.length}
              onValueChange={(next) => {
                void flush({ length: next });
              }}
              options={[
                { value: 'short', label: t('tone.lengthShort') },
                { value: 'medium', label: t('tone.lengthMedium') },
                { value: 'mirror', label: t('tone.lengthMirror') },
              ]}
              aria-label={t('tone.lengthLabel')}
              size="sm"
            />
          </div>
        </FieldAssembly>
        <FieldAssembly label={t('tone.humorLabel')} description={t('tone.humorSub')} asChild>
          <div role="group" aria-label={t('tone.humorLabel')}>
            <Segmented<'never' | 'occasional' | 'natural'>
              value={voice.humor}
              onValueChange={(next) => {
                void flush({ humor: next });
              }}
              options={[
                { value: 'never', label: t('tone.humorNever') },
                { value: 'occasional', label: t('tone.humorOccasional') },
                { value: 'natural', label: t('tone.humorNatural') },
              ]}
              aria-label={t('tone.humorLabel')}
              size="sm"
            />
          </div>
        </FieldAssembly>
        <FieldAssembly label={t('tone.emojiLabel')} description={t('tone.emojiSub')} asChild>
          <div role="group" aria-label={t('tone.emojiLabel')}>
            <Segmented<'never' | 'light' | 'yes'>
              value={voice.emoji}
              onValueChange={(next) => {
                void flush({ emoji: next });
              }}
              options={[
                { value: 'never', label: t('tone.emojiNever') },
                { value: 'light', label: t('tone.emojiLight') },
                { value: 'yes', label: t('tone.emojiYes') },
              ]}
              aria-label={t('tone.emojiLabel')}
              size="sm"
            />
          </div>
        </FieldAssembly>
        <FieldAssembly
          label={t('tone.adaptiveLabel')}
          description={t('tone.adaptiveSub')}
          hint={voice.adaptive ? t('tone.adaptiveOn') : t('tone.adaptiveOff')}
          asChild
        >
          <Switch
            aria-label={t('tone.adaptiveLabel')}
            checked={voice.adaptive}
            onCheckedChange={(next: boolean) => {
              void flush({ adaptive: next });
            }}
            data-testid="voice-adaptive-switch"
          />
        </FieldAssembly>
      </div>
    </SectionShell>
  );
}
